import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Input,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  Typography,
} from "@material-ui/core";
import {
  ExperienceBadge,
  ExperienceBookableItem,
  ExperienceDailyPrice,
  ExperienceId,
  ExperienceLanguageGuide,
  ExperienceLogistics,
  ExperienceOptionId,
  ExperiencePricingCategory,
  ExperiencePricingCategoryEnum,
  ExperiencesAgeBandEnum,
  ExperiencesShopOptionRequest,
  IExperiencesAgeBandCounts,
  IExperiencesPassengerCounts,
  ParsedExperienceBookableItem,
  ReviewSummary,
  RewardsAccount,
  TicketType,
} from "redmond";
import {
  AvailabilityReviewRating,
  AvailabilityDuration,
  AvailabilityLanguage,
  Icon,
  IconName,
  truncateString,
  B2BButton,
  ActionButton,
  ExperienceTag,
  getPriceString,
  ActionLink,
  DesktopPopupModal,
  getRewardText,
  AvailabilityTicket,
  NotificationBanner,
  BannerSeverity,
  PickerType,
  SanitizedHtml,
} from "halifax";
import dayjs from "dayjs";
import clsx from "clsx";
import { min } from "lodash-es";

import { ShopComponentSkeleton } from "../../component";
import styles from "./styles.module.scss";
import * as textConstants from "../../textConstants";
import { renderAddress, ShopMap } from "../ShopMap/component";
import {
  BadgeExcellenceModal,
  getPaxMixFromTravelersCount,
  getTotalTravelersCount,
  MonthAndDatePicker,
  MonthAndDatePickerType,
  ExperiencesMobilePopoverCard,
} from "../../../common";
import { TOTAL_NUM_TRAVELERS, START_TIME } from "../../../common/textConstants";
import { PATH_BOOK } from "../../../../utils/paths";
import { ParsedAgeBand } from "../../reducer";
import {
  AgeBandCountPickerTitles,
  PassengerCountPicker,
  PassengerCountPickerTitles,
  TravelerCountPickerTitles,
} from "../ShopPassengerCountPicker/component";

export interface IShopOverview {
  isSkeleton?: boolean;
  experienceId?: ExperienceId;
  title?: string;
  overview?: string;
  rating?: ReviewSummary;
  duration?: number;
  durationFromMinutes?: number;
  durationToMinutes?: number;
  locationLogistics?: ExperienceLogistics;
  languageGuides?: ExperienceLanguageGuide[];
  provider?: string;
  badges?: ExperienceBadge[];
  bookableItems?: ParsedExperienceBookableItem[];
  pricingCategory?: ExperiencePricingCategory;
  experienceMaxTravelers?: number;
  adultRequired?: boolean;
  ticket?: TicketType;
  dailyPrices?: ExperienceDailyPrice[];
  timeSelectOptions?: string[];
  pricingOpaque?: string;
  startingTimeZone?: string;
}

export interface IShopOverviewProps extends IShopOverview {
  setFromDate: (date: Date | null) => void;
  setUntilDate: (date: Date | null) => void;
  fromDate: Date | null;
  untilDate: Date | null;
  fetchExperiencesShop: () => void;
  fetchExperiencesShopOptions: (request: ExperiencesShopOptionRequest) => void;
  selectedBookableItem: ExperienceBookableItem | null;
  setSelectedBookableItem: (
    selectedBookableItem: ExperienceBookableItem | null
  ) => void;
  selectedOptionId?: ExperienceOptionId;
  setSelectedOptionId: (optionId: ExperienceOptionId | undefined) => void;
  selectedTime: string | null;
  setSelectedTime: (selectedTime: string) => void;
  selectedDate: string | null;
  setSelectedDate: (date: any) => void;
  setTravelerCounts: (counts: IExperiencesPassengerCounts) => void;
  travelerCounts?: IExperiencesPassengerCounts;
  mobile?: boolean;
  largestValueAccount?: RewardsAccount;
  parsedAgeBands?: { [key in ExperiencesAgeBandEnum]?: ParsedAgeBand };
  disableCheckout?: boolean;
  isDatelessSearch?: boolean;
}

export const getMinMaxHelperText = (
  minNumberOfTravelers?: number,
  maxNumberOfTravelers?: number
) => {
  let helperText = "";
  if (minNumberOfTravelers !== undefined) {
    helperText = `${helperText} Minimum: ${minNumberOfTravelers}`;
  }

  if (maxNumberOfTravelers !== undefined) {
    helperText = `${helperText}${
      helperText.length > 0 ? "," : ""
    } Maximum: ${maxNumberOfTravelers}`;
  }

  return helperText;
};

const Skeleton = () => (
  <Box className={styles["experiences-shop-skeleton"]}>
    <Box className={styles["section-wrapper"]}>
      <ShopComponentSkeleton name="small" />
      <ShopComponentSkeleton name="large" />
      <ShopComponentSkeleton name="medium" />
    </Box>
    <Box className={styles["section-wrapper"]}>
      <ShopComponentSkeleton name="medium" />
      <ShopComponentSkeleton name="large" />
      <ShopComponentSkeleton name="medium" />
    </Box>
    <Box className={styles["section-wrapper"]}>
      <ShopComponentSkeleton name="large" />
      <ShopComponentSkeleton name="small" />
      <ShopComponentSkeleton name="large" />
    </Box>
    <Box className={styles["section-wrapper"]}>
      <ShopComponentSkeleton name="medium" />
      <ShopComponentSkeleton name="small" />
      <ShopComponentSkeleton name="medium" />
    </Box>
  </Box>
);

export const ShopOverview = (props: IShopOverviewProps) => {
  const {
    isSkeleton = false,
    title,
    experienceId,
    overview,
    rating,
    duration,
    durationFromMinutes,
    durationToMinutes,
    locationLogistics,
    languageGuides,
    provider,
    badges,
    bookableItems,
    pricingCategory,
    setFromDate,
    setUntilDate,
    fromDate,
    untilDate,
    fetchExperiencesShop,
    fetchExperiencesShopOptions,
    selectedBookableItem,
    setSelectedBookableItem,
    selectedTime,
    setSelectedTime,
    setTravelerCounts,
    travelerCounts,
    selectedOptionId,
    setSelectedOptionId,
    selectedDate,
    setSelectedDate,
    experienceMaxTravelers,
    largestValueAccount,
    adultRequired,
    mobile = false,
    ticket,
    timeSelectOptions,
    disableCheckout,
    pricingOpaque,
    parsedAgeBands,
    isDatelessSearch = false,
  } = props;

  const history = useHistory();
  const [isBadgeExcellenceModalOpen, setBadgeExcellenceModalOpen] =
    React.useState(false);
  const [openCalendarModal, setOpenCalendarModal] = React.useState(false);
  const [openTravelerCountPicker, setOpenTravelerCountPicker] =
    React.useState(false);

  const [showDescription, setShowDescription] = React.useState(false);

  const [showExpandedMap, setShowExpandedMap] = React.useState(false);

  const [localFromDate, setLocalFromDate] = React.useState<Date | null>(null);
  const [localUntilDate, setLocalUntilDate] = React.useState<Date | null>(null);

  const [modalTitles, setModalTitles] = useState<
    PassengerCountPickerTitles | undefined
  >(undefined);
  const [totalTravelers, setTotalTravelers] = useState<number>(2);

  const [variantMax, setVariantMax] = useState<number | undefined>(
    experienceMaxTravelers
  );
  const isMultiVariant = useMemo(
    () => bookableItems && bookableItems.length > 1,
    [bookableItems]
  );

  const handleFetchShopOptions = (
    travelersCount: IExperiencesPassengerCounts
  ) => {
    if (selectedOptionId && selectedDate && experienceId && !mobile) {
      // Get timeSlotPrices for the selected date from the selected bookable item
      const selectedItem = bookableItems?.find(
        (item) => item.experienceOptionId.value === selectedOptionId.value
      );

      // Get timeSlotPrices and ageBandPrices for the selected date
      const timeSlotPrices = selectedItem?.timeSlotsByDate[selectedDate] || [];
      const ageBandPrices =
        selectedItem?.ageBandPricesByDate[selectedDate] || [];

      fetchExperiencesShopOptions({
        experienceId,
        experienceOptionId: selectedBookableItem?.experienceOptionId ?? selectedOptionId,
        paxMix: getPaxMixFromTravelersCount(travelersCount),
        date: selectedDate,
        currency: "USD",
        shopPricingOpaque: pricingOpaque,
        timeSlotPrices,
        ageBandPrices,
      });
    }
  };

  useEffect(() => {
    setTotalTravelers(getTotalTravelersCount(travelerCounts));
  }, [travelerCounts]);

  const unavailableDates = useMemo(
    () =>
      selectedBookableItem?.dailyPrices
        ?.filter((daily) => daily.price === undefined)
        .map((daily) => dayjs(daily.date).toDate()),
    [selectedBookableItem]
  );

  const pricePerDay: { [date: string]: string } = useMemo(() => {
    const priceDateMap: { [date: string]: string } = {};
    selectedBookableItem?.dailyPrices
      ?.filter((daily) => daily.price)
      .forEach((daily) => {
        const date = dayjs(daily.date).format("YYYY-MM-DD");
        const { price } = daily;
        if (price) {
          priceDateMap[date] = getPriceString({
            price: price.fiat.value,
            currencySymbol: price.fiat.currencySymbol,
          });
        }
      });

    return priceDateMap;
  }, [selectedBookableItem]);

  useEffect(() => {
    if (parsedAgeBands) {
      if (parsedAgeBands.Adult) {
        const newModalTitles: AgeBandCountPickerTitles = {
          modalTitle: textConstants.EDIT_TRAVELERS,
          adultTitle: textConstants.ADULTS,
          adultSubtitle: textConstants.AGES_SUBTITLE(
            parsedAgeBands.Adult?.minAge,
            parsedAgeBands.Adult?.maxAge
          ),
          adultHelperText: parsedAgeBands.Adult
            ? getMinMaxHelperText(
                parsedAgeBands.Adult.minNumberOfTravelers,
                parsedAgeBands.Adult.maxNumberOfTravelers
              )
            : undefined,
          adultPrice: parsedAgeBands.Adult
            ? textConstants.makePriceString(parsedAgeBands.Adult)
            : undefined,
          childrenPrice: parsedAgeBands.Child
            ? textConstants.makePriceString(parsedAgeBands.Child)
            : undefined,
          childrenTitle: parsedAgeBands.Child ? textConstants.CHILDREN : "",
          childrenSubtitle: textConstants.AGES_SUBTITLE(
            parsedAgeBands.Child?.minAge,
            parsedAgeBands.Child?.maxAge
          ),
          childrenHelperText: parsedAgeBands.Child
            ? getMinMaxHelperText(
                parsedAgeBands.Child.minNumberOfTravelers,
                parsedAgeBands.Child.maxNumberOfTravelers
              )
            : undefined,
          seniorTitle: parsedAgeBands.Senior
            ? textConstants.SENIORS
            : undefined,
          seniorSubtitle: textConstants.AGES_SUBTITLE(
            parsedAgeBands.Senior?.minAge,
            parsedAgeBands.Senior?.maxAge
          ),
          seniorHelperText: parsedAgeBands.Senior
            ? getMinMaxHelperText(
                parsedAgeBands.Senior.minNumberOfTravelers,
                parsedAgeBands.Senior.maxNumberOfTravelers
              )
            : undefined,
          seniorPrice: parsedAgeBands.Senior
            ? textConstants.makePriceString(parsedAgeBands.Senior)
            : undefined,
          youthTitle: parsedAgeBands.Youth ? textConstants.YOUTHS : undefined,
          youthSubtitle: textConstants.AGES_SUBTITLE(
            parsedAgeBands.Youth?.minAge,
            parsedAgeBands.Youth?.maxAge
          ),
          youthHelperText: parsedAgeBands.Youth
            ? getMinMaxHelperText(
                parsedAgeBands.Youth.minNumberOfTravelers,
                parsedAgeBands.Youth.maxNumberOfTravelers
              )
            : undefined,
          youthPrice: parsedAgeBands.Youth
            ? textConstants.makePriceString(parsedAgeBands.Youth)
            : undefined,
          infantTitle: parsedAgeBands.Infant
            ? textConstants.INFANTS
            : undefined,
          infantSubtitle: textConstants.AGES_SUBTITLE(
            parsedAgeBands.Infant?.minAge,
            parsedAgeBands.Infant?.maxAge
          ),
          infantHelperText: parsedAgeBands.Infant
            ? getMinMaxHelperText(
                parsedAgeBands.Infant.minNumberOfTravelers,
                parsedAgeBands.Infant.maxNumberOfTravelers
              )
            : undefined,
          infantPrice: parsedAgeBands.Infant
            ? textConstants.makePriceString(parsedAgeBands.Infant)
            : undefined,
        };
        setModalTitles(newModalTitles);
      } else if (parsedAgeBands.Traveler) {
        const newModalTitles: TravelerCountPickerTitles = {
          modalTitle: textConstants.EDIT_TRAVELERS,
          travelerTitle: textConstants.TRAVELER,
          travelerSubtitle: textConstants.AGES_SUBTITLE(
            parsedAgeBands.Traveler?.minAge,
            parsedAgeBands.Traveler?.maxAge
          ),
          travelerHelperText: parsedAgeBands.Traveler
            ? getMinMaxHelperText(
                parsedAgeBands.Traveler.minNumberOfTravelers,
                parsedAgeBands.Traveler.maxNumberOfTravelers
              )
            : undefined,
          travelerPrice: parsedAgeBands.Traveler
            ? textConstants.makePriceString(parsedAgeBands.Traveler)
            : undefined,
        };
        setModalTitles(newModalTitles);
      }

      let newVariantMax = 0;

      Object.values(parsedAgeBands).forEach((value) => {
        if (value.maxNumberOfTravelers !== undefined) {
          newVariantMax += value.maxNumberOfTravelers;
        } else {
          newVariantMax = experienceMaxTravelers ?? Infinity;
        }
      });

      setVariantMax(newVariantMax);
    }
  }, [parsedAgeBands, selectedBookableItem, selectedDate, travelerCounts]);

  useEffect(() => {
    if (bookableItems) {
      setSelectedBookableItem(bookableItems[0]);
      if (bookableItems[0].experienceOptionId) {
        setSelectedOptionId(bookableItems[0].experienceOptionId);
      }
    }
  }, [bookableItems]);

  useEffect(() => {
    if (selectedBookableItem) {
      if (selectedBookableItem.experienceOptionId) {
        setSelectedOptionId(selectedBookableItem.experienceOptionId);
      }
      const dateToSelect = Object.keys(selectedBookableItem.timeSlotsByDate)[0];
      setSelectedDate(dateToSelect);
    }
  }, [selectedBookableItem]);

  useEffect(() => {
    if (timeSelectOptions) {
      setSelectedTime(timeSelectOptions[0]);
    }
  }, [timeSelectOptions]);

  useEffect(() => {
    if (selectedOptionId && selectedDate && travelerCounts) {
      handleFetchShopOptions(travelerCounts);
    }
  }, [selectedOptionId, selectedDate]);

  useEffect(() => {
    setLocalFromDate(fromDate);
  }, [fromDate]);

  useEffect(() => {
    setLocalUntilDate(untilDate);
  }, [untilDate]);

  const handleClickDone = () => {
    setOpenCalendarModal(false);
    let refetchExperiencesShop = false;
    if (localFromDate !== fromDate || localUntilDate !== untilDate) {
      refetchExperiencesShop = true;
    }
    setFromDate(localFromDate);
    setUntilDate(localUntilDate);
    if (refetchExperiencesShop) {
      setSelectedDate(null);
      setSelectedOptionId(undefined);
      fetchExperiencesShop();
      window.scrollTo(0, 0);
    }
  };

  if (
    !title ||
    !overview ||
    !rating ||
    duration === undefined ||
    !locationLogistics ||
    !provider ||
    !languageGuides
  ) {
    return null;
  }

  const sameAsStartLocation =
    locationLogistics.startLocations?.[0]?.address ===
    locationLogistics.endLocations?.[0]?.address;

  const renderExperienceDetailsTopSection = () => (
    <Box className={styles["experiences-shop-overview-subtext"]}>
      <AvailabilityReviewRating
        reviewCount={rating.numberOfReviews}
        scaledScore={rating.reviewAverage}
        shortReviews
      />
      <Divider
        className={styles["experiences-shop-overview-subtext-dividers"]}
        orientation="vertical"
      />
      {duration && (
        <>
          <AvailabilityDuration
            duration={duration}
            durationFromMinutes={durationFromMinutes}
            durationToMinutes={durationToMinutes}
            isSkeleton={isSkeleton}
          />
          <Divider
            className={styles["experiences-shop-overview-subtext-dividers"]}
            orientation="vertical"
          />
        </>
      )}
      {languageGuides && languageGuides.length > 0 && (
        <>
          <AvailabilityLanguage
            languages={languageGuides}
            isSkeleton={isSkeleton}
          />
          <Divider
            className={styles["experiences-shop-overview-subtext-dividers"]}
            orientation="vertical"
          />
        </>
      )}
      {ticket && (
        <>
          <AvailabilityTicket ticket={ticket} />
          <Divider
            className={styles["experiences-shop-overview-subtext-dividers"]}
            orientation="vertical"
          />
        </>
      )}
      <Typography className={styles["experiences-shop-overview-title"]}>
        {truncateString(provider, 50)}
      </Typography>
    </Box>
  );

  const renderOverview = (description: string) => {
    if (!description) return null;

    const truncatedDescription = truncateString(description, 300, true);

    return (
      <>
        {!mobile && <Typography variant="h6">Overview</Typography>}
        <Typography
          variant="body2"
          className={styles["experiences-shop-overview-text"]}
        >
          {!showDescription ? truncatedDescription : description}
        </Typography>
        {description !== truncatedDescription && (
          <ActionLink
            className={styles["experiences-shop-overview-button"]}
            onClick={() => setShowDescription(!showDescription)}
            content={
              showDescription
                ? textConstants.SHOW_LESS
                : textConstants.READ_MORE
            }
          />
        )}
      </>
    );
  };

  const renderBookableItem = (item: ExperienceBookableItem) => {
    // On desktop dateless, the date/time/traveler selectors move to
    // ShopSelectionButtons in the right checkout panel — hide them here.
    // Mobile still renders them in ShopMobileAvailabilityCard as before.
    const hideSelections = isDatelessSearch && !mobile;

    return (
      <Box className={styles["experiences-shop-overview-bookable-section"]}>
        {isMultiVariant && renderOverview(item.description)}
        {!hideSelections && (
          <>
            <Box className={styles["experiences-shop-overview-availability"]}>
              <Typography variant="h6">{textConstants.AVAILABILITY}</Typography>
              <Box
                className={
                  styles[
                    "experiences-shop-overview-availability-selections-container"
                  ]
                }
              >
                {item.datesPastSearchedRange && (
                  <NotificationBanner
                    className={styles["unavailable-dates-banner"]}
                    label={textConstants.AVAILABILITY_EXCEEDS_DATE_RANGE}
                    severity={BannerSeverity.INFO}
                    icon={
                      <Icon
                        className="banner-icon"
                        name={IconName.CalendarWithBoxIcon}
                      />
                    }
                  />
                )}
                <Box
                  className={clsx(
                    styles["experiences-shop-overview-availability-selections"]
                  )}
                >
                  {/* timeSlotByDate will return the next 6 bookable dates and times, we only want to show 3 or 4 depending on type of bookable item */}
                  {Object.keys(item.timeSlotsByDate)
                    .slice(0, isMultiVariant ? 3 : 4)
                    .map((date) => {
                      const { price } = item.timeSlotsByDate[date][0];
                      return price ? (
                        <ActionButton
                          className={clsx(
                            styles[
                              "experiences-shop-overview-bookable-options-button"
                            ],
                            {
                              [styles["selected-option"]]: date === selectedDate,
                            }
                          )}
                          defaultStyle={
                            date === selectedDate ? "h4r-primary" : "h4r-secondary"
                          }
                          onClick={() => {
                            if (date !== selectedDate) {
                              setSelectedDate(date);
                            }
                          }}
                          message={
                            <Box
                              className={
                                styles[
                                  "experiences-shop-overview-bookable-options-button-label"
                                ]
                              }
                            >
                              <span
                                className={
                                  styles[
                                    "experiences-shop-overview-bookable-options-button-date"
                                  ]
                                }
                              >
                                {dayjs(date).format("ddd, MMM D")}
                              </span>
                              <span
                                className={
                                  styles[
                                    "experiences-shop-overview-bookable-options-button-price"
                                  ]
                                }
                              >
                                {textConstants.PRICE_TITLE(price, pricingCategory)}
                              </span>
                            </Box>
                          }
                        />
                      ) : null;
                    })}
                  <ActionLink
                    content={
                      <Box className={styles["view-all-calendar-link-content"]}>
                        <Icon name={IconName.CalendarWithBoxIcon} />
                        <Typography className={styles["view-all-calendar-link-text"]}>
                          {textConstants.VIEW_ALL}
                        </Typography>
                      </Box>
                    }
                    onClick={() => setOpenCalendarModal(true)}
                    className={styles["view-all-calendar-link"]}
                  />
                </Box>
              </Box>
            </Box>
            <Box className={styles["experiences-shop-overview-bookables"]}>
              {selectedTime ? (
                <Typography variant="h6">
                  {textConstants.CONFIRM_TRAVELERS_WITH_START_TIME}
                </Typography>
              ) : (
                <Typography variant="h6">
                  {textConstants.CONFIRM_TRAVELERS}
                </Typography>
              )}
              <Box
                className={styles["experiences-shop-overview-bookable-selections"]}
              >
                <B2BButton
                  aria-label={TOTAL_NUM_TRAVELERS(totalTravelers)}
                  className="num-travelers-input b2b"
                  variant="traveler-selector"
                  onClick={() => setOpenTravelerCountPicker(true)}
                >
                  <Box className={styles["num-traveler-content"]}>
                    <Box className={styles["num-traveler-with-icon"]}>
                      <Icon
                        aria-hidden
                        className={styles["num-traveler-icon-start"]}
                        name={IconName.B2BUser}
                        ariaLabel=""
                      />
                      <Box className={styles["num-traveler-text"]}>
                        {TOTAL_NUM_TRAVELERS(totalTravelers)}
                      </Box>
                    </Box>
                    <Icon
                      aria-hidden
                      className={styles["num-traveler-icon-end"]}
                      name={IconName.B2BEditPencil}
                      ariaLabel=""
                    />
                  </Box>
                </B2BButton>
                {selectedTime && timeSelectOptions && (
                  <FormControl className={styles["time-input-container"]}>
                    <InputLabel id="time-select-label">{START_TIME}</InputLabel>
                    <Select
                      labelId="time-select-label"
                      id="time-select"
                      className={styles["time-selection-menu"]}
                      classes={{
                        root: styles["time-select-root"],
                        icon: styles["time-select-arrow-icon"],
                      }}
                      label={START_TIME}
                      onChange={(event) => {
                        setSelectedTime(event.target.value as string);
                      }}
                      value={selectedTime}
                      MenuProps={{
                        classes: {
                          paper: clsx(
                            styles["time-selector-menu"],
                            "time-selector-menu"
                          ),
                        },
                      }}
                      input={
                        <Input
                          className={styles["input-root"]}
                          disableUnderline
                          startAdornment={
                            <Icon
                              className={styles["clock-icon"]}
                              name="clock-icon"
                            />
                          }
                        />
                      }
                    >
                      {timeSelectOptions.map((timeOption) => (
                        <MenuItem value={timeOption} key={timeOption}>
                          {timeOption}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
              <ActionButton
                className={styles["experiences-shop-overview-checkout-button"]}
                message={
                  <span
                    className={
                      styles["experiences-shop-overview-checkout-button-text"]
                    }
                  >
                    {textConstants.CONTINUE_TO_CHECKOUT}
                  </span>
                }
                defaultStyle="h4r-primary"
                onClick={() => history.push(`${PATH_BOOK}${history.location.search}`)}
                disabled={disableCheckout}
              />
            </Box>
          </>
        )}
      </Box>
    );
  };

  const renderExpandedVariantLabel = (item: ExperienceBookableItem) => (
    <Box
      className={clsx(
        styles["experiences-shop-bookable-item-option-container"],
        styles["experiences-shop-expanded-bookable-item-option-container"]
      )}
    >
      <Typography
        id={item.experienceOptionId.value}
        variant="h6"
        className={styles["experiences-shop-bookable-item-option-title"]}
      >
        {item.title}
      </Typography>
      {renderBookableItem(item)}
    </Box>
  );

  const renderCollapsedVariantLabel = (item: ParsedExperienceBookableItem) => {
    const { firstAvailablePrice } = item;

    const priceTitle = firstAvailablePrice?.price
      ? textConstants.PRICE_TITLE(firstAvailablePrice.price, pricingCategory)
      : "";

    const rewardsTitle =
      firstAvailablePrice?.price && largestValueAccount
        ? `${getRewardText({
            reward:
              firstAvailablePrice?.price.rewards[
                largestValueAccount.accountReferenceId
              ],
          })}`
        : "";

    let subtitle = experienceMaxTravelers
      ? textConstants.MAX_TRAVELER_TEXT(experienceMaxTravelers)
      : undefined;

    if (
      parsedAgeBands?.Adult?.priceVariesByCount ||
      parsedAgeBands?.Traveler?.priceVariesByCount
    ) {
      subtitle = textConstants.PRICE_VARIES_ON_GROUP_SIZE;
    }

    return (
      <Box
        className={clsx(
          styles["experiences-shop-bookable-item-option-container"],
          styles["experiences-shop-collapsed-bookable-item-option-container"]
        )}
      >
        <Typography
          id={item.experienceOptionId.value}
          variant="h6"
          className={styles["experiences-shop-bookable-item-option-title"]}
        >
          {item.title}
        </Typography>
        {priceTitle && (
          <Box
            className={clsx(
              styles["experiences-shop-bookable-item-option-price-container"]
            )}
          >
            <SanitizedHtml
              as={Typography}
              className={styles["experiences-shop-bookable-item-option-price"]}
              html={textConstants.getPriceAndRewardsCopy({
                  price: priceTitle,
                  rewards: rewardsTitle,
                })}
            />
            {subtitle && (
              <Typography
                className={
                  styles["experiences-shop-bookable-item-option-subtitle"]
                }
                variant="body2"
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const renderMultiVariantBookableItems = () => (
    <Box>
      <Box className={styles["experiences-shop-bookable-item-selection"]}>
        {(bookableItems || []).map((item) => (
          <FormControlLabel
            className={styles["experiences-shop-bookable-item-option"]}
            key={item.experienceOptionId.value}
            value={item.experienceOptionId.value}
            label={
              item.experienceOptionId.value === selectedOptionId?.value
                ? renderExpandedVariantLabel(item)
                : renderCollapsedVariantLabel(item)
            }
            labelPlacement="end"
            control={
              <Radio
                checked={
                  selectedOptionId?.value === item.experienceOptionId?.value
                }
                className={clsx(
                  styles["experiences-shop-bookable-item-option-radio"],
                  {
                    [styles[
                      "experiences-shop-bookable-item-option-radio-checked"
                    ]]:
                      selectedOptionId?.value ===
                      item.experienceOptionId?.value,
                  }
                )}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  if (event.target.checked) {
                    setSelectedBookableItem(item);
                  }
                }}
              />
            }
          />
        ))}
      </Box>
    </Box>
  );

  const renderDesktopVersion = () => (
    <Box className={styles["experiences-shop-overview-container"]}>
      {badges && badges.length > 0 && (
        <Box className={styles["experience-shop-overview-tags-container"]}>
          {badges.map((badge) => (
            <ExperienceTag
              key={badge}
              badge={badge}
              onBadgeClick={
                badge === ExperienceBadge.BadgeOfExcellence
                  ? () => {
                      if (!isBadgeExcellenceModalOpen)
                        setBadgeExcellenceModalOpen(true);
                    }
                  : undefined
              }
            />
          ))}
        </Box>
      )}
      {/* TOP SECTION */}
      {renderExperienceDetailsTopSection()}
      {/* MIDDLE SECTION */}
      {bookableItems && (
        <Box className={styles["experiences-shop-bookable-items-section"]}>
          {isMultiVariant
            ? renderMultiVariantBookableItems()
            : renderBookableItem(bookableItems[0])}
        </Box>
      )}

      {/* BOTTOM SECTION */}
      <Box className={styles["experiences-shop-overview-bottom-section"]}>
        {!isMultiVariant && renderOverview(overview)}
        <Box className={styles["experiences-shop-overview-map-section"]}>
          {locationLogistics.startLocations.length > 0 && (
            <Box
              className={styles["experiences-shop-overview-map-starting-point"]}
            >
              <Typography
                variant="h6"
                className={styles["experiences-shop-overview-map-titles"]}
              >
                <Icon name={IconName.B2BMapPin} />
                {textConstants.STARTING_POINT}
              </Typography>
              {renderAddress(locationLogistics.startLocations[0])}
            </Box>
          )}
          {locationLogistics.endLocations.length > 0 && (
            <Box className={styles["experiences-shop-overview-map-end-point"]}>
              <Typography
                variant="h6"
                className={styles["experiences-shop-overview-map-titles"]}
              >
                <Icon name={IconName.FlagIcon} />
                {textConstants.END_POINT}
              </Typography>
              {renderAddress(
                locationLogistics.endLocations[0],
                sameAsStartLocation
              )}
            </Box>
          )}
          {locationLogistics.startLocations.length > 0 &&
            locationLogistics.endLocations.length > 0 && (
              <Box className={styles["experiences-shop-overview-map"]}>
                <ShopMap
                  locationLogistics={locationLogistics}
                  setShowExpandedlMap={setShowExpandedMap}
                  variant="small"
                  sameAsStart={sameAsStartLocation}
                />
              </Box>
            )}
        </Box>
      </Box>
      {!isDatelessSearch && (
        <>
          <DesktopPopupModal
            open={openCalendarModal}
            onClose={(event: React.MouseEvent) => {
              event.stopPropagation();
              setOpenCalendarModal(false);
            }}
            className={clsx(
              "desktop-calendar-picker-popup-root",
              "desktop-experiences-calendar-picker-popup-root",
              "experiences-module"
            )}
            contentClassName="desktop-calendar-picker-wrapper"
            invisibleBackdrop={false}
          >
            {/* dailyPrices: Array<ExperienceDailyPrice>; */}
            <MonthAndDatePicker
              pickerType={PickerType.DAY}
              viewType={MonthAndDatePickerType.Horizontal}
              startDate={localFromDate}
              endDate={localUntilDate}
              setStartDate={(val: Date | null) => {
                setLocalFromDate(val);
                setLocalUntilDate(val);
              }}
              setEndDate={() => {}}
              header={textConstants.SELECT_DATE}
              subheader={
                pricingCategory?.ExperiencePricingCategory ===
                ExperiencePricingCategoryEnum.PerGroup
                  ? textConstants.PRICE_PER_GROUP
                  : textConstants.PRICE_PER_ADULT
              }
              className={clsx("experiences-shop-date-picker", "b2b")}
              unavailableDates={unavailableDates}
              pricePerDay={pricePerDay}
            />
            <Button
              onClick={handleClickDone}
              disabled={!localFromDate || !localUntilDate}
              className="select-dates-button"
              variant="contained"
            >
              {textConstants.SELECT_DATE}
            </Button>
          </DesktopPopupModal>
          {travelerCounts && setTravelerCounts && parsedAgeBands && modalTitles && (
            <DesktopPopupModal
              open={openTravelerCountPicker}
              className={styles["desktop-experiences-passenger-picker-popup-root"]}
              contentClassName={
                styles["desktop-passenger-count-picker-popup-container"]
              }
              onClose={() => setOpenTravelerCountPicker(false)}
              invisibleBackdrop={false}
            >
              <PassengerCountPicker
                minimumAdultsCount={0}
                titles={modalTitles}
                setPassengerCounts={(counts) => {
                  const travellers = counts as IExperiencesPassengerCounts;
                  setTravelerCounts(travellers);
                  handleFetchShopOptions(travellers);
                }}
                onClickApply={() => {
                  setOpenTravelerCountPicker(false);
                }}
                counts={travelerCounts}
                className="b2b"
                maximumTravelersCount={
                  min([experienceMaxTravelers, variantMax]) ?? 0
                }
                paxCountLimits={
                  parsedAgeBands.Adult
                    ? {
                        seniorsCount: {
                          min: parsedAgeBands?.Senior?.minNumberOfTravelers,
                          max: parsedAgeBands?.Senior?.maxNumberOfTravelers,
                        },
                        adultsCount: {
                          min:
                            parsedAgeBands?.Adult?.minNumberOfTravelers ||
                            parsedAgeBands?.Traveler?.minNumberOfTravelers,
                          max:
                            parsedAgeBands?.Adult?.maxNumberOfTravelers ||
                            parsedAgeBands?.Traveler?.maxNumberOfTravelers,
                        },
                        youthsCount: {
                          min: parsedAgeBands?.Youth?.minNumberOfTravelers,
                          max: parsedAgeBands?.Youth?.maxNumberOfTravelers,
                        },
                        childrenCount: {
                          min: parsedAgeBands?.Child?.minNumberOfTravelers,
                          max: parsedAgeBands?.Child?.maxNumberOfTravelers,
                        },
                        infantsCount: {
                          min: parsedAgeBands?.Infant?.minNumberOfTravelers,
                          max: parsedAgeBands?.Infant?.maxNumberOfTravelers,
                        },
                      }
                    : {
                        travelersCount: {
                          min: parsedAgeBands?.Traveler?.minNumberOfTravelers,
                          max: parsedAgeBands?.Traveler?.maxNumberOfTravelers,
                        },
                      }
                }
                getPaxCountsError={(counts) => {
                  const passengerCounts = counts as IExperiencesAgeBandCounts;
                  let errorText = "";
                  if (adultRequired) {
                    errorText =
                      passengerCounts.adultsCount +
                        (passengerCounts.seniorsCount ?? 0) <=
                      0
                        ? textConstants.ADULT_REQUIRED
                        : "";
                  }

                  return errorText;
                }}
              />
            </DesktopPopupModal>
          )}
        </>
      )}
      {locationLogistics && (
        <DesktopPopupModal
          open={showExpandedMap}
          onClose={(event: React.MouseEvent) => {
            event.stopPropagation();
            setShowExpandedMap(false);
          }}
          className={clsx(styles["desktop-experiences-shop-map-popup-root"])}
          contentClassName={styles["desktop-shop-map-wrapper"]}
          invisibleBackdrop={false}
        >
          <div className={styles["experiences-shop-map-header-container"]}>
            <Typography
              variant="h3"
              className={styles["experiences-shop-map-title"]}
            >
              {title}
            </Typography>
            {renderExperienceDetailsTopSection()}
          </div>
          <ShopMap
            locationLogistics={locationLogistics}
            setShowExpandedlMap={setShowExpandedMap}
            variant="large"
            sameAsStart={sameAsStartLocation}
          />
        </DesktopPopupModal>
      )}
      <BadgeExcellenceModal
        isOpen={isBadgeExcellenceModalOpen}
        isMobile={mobile}
        onClose={() => {
          setBadgeExcellenceModalOpen(false);
        }}
      />
      {isSkeleton ? <Skeleton /> : null}
    </Box>
  );

  const renderMobileVersion = () => (
    <Box
      className={clsx(
        styles["experiences-shop-overview-container"],
        styles.mobile
      )}
    >
      {renderOverview(overview)}
      <Box className={styles["experiences-shop-overview-map-section"]}>
        {locationLogistics.startLocations.length > 0 && (
          <Box
            className={styles["experiences-shop-overview-map-starting-point"]}
          >
            <Typography
              variant="h6"
              className={styles["experiences-shop-overview-map-titles"]}
            >
              <Icon name={IconName.B2BMapPin} />
              {textConstants.STARTING_POINT}
            </Typography>
            {renderAddress(locationLogistics.startLocations[0])}
          </Box>
        )}
        {locationLogistics.endLocations.length > 0 && (
          <Box className={styles["experiences-shop-overview-map-end-point"]}>
            <Typography
              variant="h6"
              className={styles["experiences-shop-overview-map-titles"]}
            >
              <Icon name={IconName.FlagIcon} />
              {textConstants.END_POINT}
            </Typography>
            {renderAddress(
              locationLogistics.endLocations[0],
              sameAsStartLocation
            )}
          </Box>
        )}
        {locationLogistics.startLocations.length > 0 &&
          locationLogistics.endLocations.length > 0 && (
            <Box className={styles["experiences-shop-overview-map"]}>
              <ShopMap
                locationLogistics={locationLogistics}
                setShowExpandedlMap={setShowExpandedMap}
                variant="small"
                sameAsStart={sameAsStartLocation}
              />
            </Box>
          )}
      </Box>
      <ExperiencesMobilePopoverCard
        headerText={
          <Box className={styles["experiences-shop-map-header-container"]}>
            <div className={styles["experiences-shop-map-header-title"]}>
              {title}
            </div>
            <div className={styles["experiences-shop-map-header-provider"]}>
              {provider}
            </div>
          </Box>
        }
        content={
          <ShopMap
            locationLogistics={locationLogistics}
            setShowExpandedlMap={setShowExpandedMap}
            variant="large"
            sameAsStart={sameAsStartLocation}
          />
        }
        isOpen={showExpandedMap}
        setOpen={setShowExpandedMap}
        className={styles["experiences-shop-map-popover"]}
      />
    </Box>
  );

  return mobile ? renderMobileVersion() : renderDesktopVersion();
};
